import { handle } from 'redux-pack';
import {
	SET_MODERATED_QUESTIONS,
	SET_TEMPLATE_ACCORDION,
	SET_SPACING_ACCORDION,
	SET_WALLPAPER_ACCORDION,
	SET_BACKGROUND_ACCORDION,
	SET_GREENROOM_SETTINGS_ACCORDION,
	SET_ASK_TO_JOIN,
	SET_SELECTED_BROADCAST_RESOLUTION,
	SET_SETTINGS_TAB,
	SET_ENTRY_WITHOUT_PRODUCER,
	SET_OVERLAY_VISIBLE,
	SET_BACKSTAGE_COMMS_ACTIVE,
	SET_USE_DEFAULT_ENDPOINT,
	SET_DEFAULT_RESOLUTION,
	REPLACE_STATE,
	SET_USE_EMAIL_INVITES,
	SET_CLEAR_SHOT,
	SET_DEFAULT_MEETING_IMAGE,
	SET_FEED_SCALING,
	SYNC_SETTINGS_REDUCER_STATE,
	GET_FEATURE_FLAGS,
	GET_FEATURE_FLAG_OVERRIDES,
	SET_HAS_SYNCED,
	UPDATE_FEATURE_FLAG,
	SET_PRESENTER_AUDIENCE_TAB,
} from '../actions/settings';
import { V_LAYOUTS } from '../../utils/video-layout';
import Socket from '../../connection/socket';
import { SaveSettings } from '../../connection/greenroom';
import { SelectGroupOption } from '../../components/general-components/select-group';
import { syncedReduxActions } from '../../utils/sync-constants';
import type {
	AppSettings,
	LayoutSpacingKey,
	LayoutSpacing,
	LayoutSpacingPreset,
	StreamMedia,
	FeatureFlags,
	SettingsReducerState,
	DefaultMeetingImage,
	CustomDestination,
} from '../types/settings';

export const RESOLUTIONS = {
	'480p': '852x480',
	'720p': '1280x720',
	'1080p': '1920x1080',
};

export const SETTINGS_TABS = {
	EVENT: 0,
	LAYOUTS: 1,
	TITLES: 2,
	CANVAS: 3,
	STREAMING: 4,
};

export const ENTRY_TYPE = {
	NO_REQUIREMENTS: 0,
	REQUIRE_APPROVAL: 1,
	REQUIRE_PRESENCE: 2,
	REQUIRE_APPROVAL_PRESENCE: 3,
	// there is no longer a way to set REQUIRE_APPROVAL_PRESENCE
	// via the ui but it is still in the db so I am leaving it
};

export const ROOM_STATES = {
	UNSET: 0,
	SHOWTIME: 1,
	EDITING: 2,
	REHEARSAL: 3,
};

export const ENTRY_OPTIONS: SelectGroupOption[] = [
	{ label: 'None', value: ENTRY_TYPE.NO_REQUIREMENTS },
	{ label: 'Producer\'s Approval', value: ENTRY_TYPE.REQUIRE_APPROVAL },
	{ label: 'Producer\'s Presence', value: ENTRY_TYPE.REQUIRE_PRESENCE },
];

let socket: Socket | null = null;
// eslint-disable-next-line
let messageTimeout: NodeJS.Timeout;
// eslint-disable-next-line
let storageTimeout: NodeJS.Timeout;
// let syncTimeout: NodeJS.Timeout;

export const defaultLayouts = [
	V_LAYOUTS.ONE.FULL,
	V_LAYOUTS.TWO.VERTICAL.COVER,
	V_LAYOUTS.THREE.VERTICAL.COVER,
	V_LAYOUTS.FOUR.GRID,
	V_LAYOUTS.FIVE.STACK.FOUR_BY_THREE,
	V_LAYOUTS.SIX.GRID,
	V_LAYOUTS.SEVEN.GRID,
	V_LAYOUTS.EIGHT.GRID,
];

const defaultSpacingPresets = [
	V_LAYOUTS.ONE.HALF,
	V_LAYOUTS.ONE.FULL_PADDING,
	V_LAYOUTS.TWO.VERTICAL.COVER,
	V_LAYOUTS.TWO.STACK.RIGHT.CONTAIN,
	V_LAYOUTS.TWO.HORIZONTAL.CONTAIN,
	V_LAYOUTS.THREE.VERTICAL.COVER,
	V_LAYOUTS.THREE.STACK.TOP,
	V_LAYOUTS.THREE.STACK.LEFT,
	V_LAYOUTS.THREE.STACK.RIGHT,
	V_LAYOUTS.FOUR.GRID,
	V_LAYOUTS.FOUR.VERTICAL.COVER,
	V_LAYOUTS.FOUR.SPIRAL,
	V_LAYOUTS.FIVE.STACK.FOUR_BY_THREE,
	V_LAYOUTS.FIVE.STACK.RIGHT,
	V_LAYOUTS.FIVE.STACK.TOP_LEFT,
	V_LAYOUTS.FIVE.STACK.TWO_LEFT,
	V_LAYOUTS.FIVE.STACK.FOUR_TOP,
	V_LAYOUTS.SIX.GRID,
	V_LAYOUTS.SIX.STACK.RIGHT,
	V_LAYOUTS.SIX.STACK.LEFT,
	V_LAYOUTS.SIX.STACK.RANDOM,
	V_LAYOUTS.SEVEN.GRID,
	V_LAYOUTS.SEVEN.STACK.CENTER,
	V_LAYOUTS.EIGHT.GRID,
].map((layout: string) => {
	const spacing: LayoutSpacing = {
		top: 5,
		bottom: 5,
		side: 5,
		inner: 5,
	};

	if (
		layout === V_LAYOUTS.ONE.HALF
		|| layout === V_LAYOUTS.TWO.VERTICAL.COVER
		|| layout === V_LAYOUTS.THREE.VERTICAL.COVER
	) {
		spacing.top = 10;
		spacing.bottom = 10;
		spacing.side = 10;
		spacing.inner = layout === V_LAYOUTS.ONE.HALF ? 10 : 0;
	}

	if (layout === V_LAYOUTS.ONE.FULL_PADDING) {
		spacing.top = 12;
		spacing.bottom = 12;
		spacing.side = 20;
		spacing.inner = 11;
	}

	if (layout === V_LAYOUTS.FOUR.VERTICAL.COVER) {
		spacing.top = 20;
		spacing.bottom = 20;
		spacing.inner = 0;
	}

	if (layout === V_LAYOUTS.FOUR.SPIRAL) {
		spacing.side = 20;
		spacing.inner = 0;
	}

	return {
		layout,
		isLinked: false,
		spacing,
	};
});

export const baseSettings: AppSettings = {
	gap: 1,
	layout: 'single cover',
	backgroundColor: '#000000',
	wallpaperType: 'color',
	wallpaperColor: '#000000',
	wallpaperImage: '',
	wallpaperScale: 100,
	backgroundType: 'color',
	backgroundImage: '',
	backgroundVideo: '',
	backgroundScale: 100,
	backgroundFill: 'cover',
	spacingLinked: false,
	topSpacing: 0,
	bottomSpacing: 0,
	sideSpacing: 0,
	innerSpacing: 0,
	presenterVideoBorderOn: true,
	presenterVideoBorderPosition: 'outside',
	presenterVideoBorderColor: '#ffffff',
	presenterVideoBorderOpacity: 1,
	presenterVideoBorderSize: 2,
	presenterVideoRadius: 0,
	titlesOn: false,
	titlesUseName: true,
	titlesUseTitle: true,
	titlesUseLocation: true,
	titlesAnimation: 'none',
	titlesDuration: null,
	titlesPosition: 'bottom-left',
	titlesFontColor1: '#ffffff',
	titlesFontColor2: '#ffffff',
	titlesBackgroundColor1: '#000000',
	titlesBackgroundColor2: '#2b2b2b',
	titlesBackgroundColor3: '#5b5b5b',
	titlesScale: 100,
	titlesSelectedCard: 1,
	layoutSpacingPresets: defaultSpacingPresets,
};
// here we have two sets of settings, queued is for changes that
// haven't been applied yet and is used in the settings panel
// applied is for the main video panel
export const initialSettingsState: SettingsReducerState = {
	templateAccordionOpen: false, 		// accordion states in settings tab
	spacingAccordionOpen: false,
	wallpaperAccordionOpen: false,
	backgroundAccordionOpen: false,
	greenroomSettingsAccordionOpen: false,
	useModeratedQuestions: true,		// moderated questions should be on by default
	askToJoin: false,
	selectedResolution: '1080p',	// This is the resolution that will be sent to the broadcast server when GO LIVE is clicked. It changes when any of the destinations, default or custom, switch
	settingsTab: SETTINGS_TABS.LAYOUTS,
	entryWithoutProducer: true,
	overlayVisible: false,
	backstageCommsActive: true,
	useDefaultEndpoint: true,
	defaultResolution: '1080p',	// This is the selected value for the default destination. So when the default destination resolution changes, this changes and is what you see in the dropdown, and selectedResolution also changes
	customDestinations: [],
	selectedCustomDestination: '',
	useEmailInvites: true,
	clearShot: false,
	useShowboard: true,
	defaultMeetingImage: {
		image: '',
		color: '',
		scale: 0,
	},
	feedScaling: true,
	presenterAudienceTab: true,
	featureFlags: {
		use_title_library: false,
	},
	hasFetchedFeatureFlags: false,
	hasSynced: false,
	hasSettingsFromBeehive: false,
	hasFetchedFFOverrides: false,
};

interface Action {
	type: string,
	payload: any,
	remote?: boolean
}

const actions: Map<any, Function> = new Map([
	[SET_TEMPLATE_ACCORDION, (state: SettingsReducerState, action: Action): SettingsReducerState => (
		{ ...state, templateAccordionOpen: action.payload })],
	[SET_SPACING_ACCORDION, (state: SettingsReducerState, action: Action): SettingsReducerState => (
		{ ...state, spacingAccordionOpen: action.payload })],
	[SET_WALLPAPER_ACCORDION, (state: SettingsReducerState, action: Action): SettingsReducerState => (
		{ ...state, wallpaperAccordionOpen: action.payload })],
	[SET_BACKGROUND_ACCORDION,
		(state: SettingsReducerState, action: Action): SettingsReducerState => (
			{ ...state, backgroundAccordionOpen: action.payload })],
	[SET_GREENROOM_SETTINGS_ACCORDION,
		(state: SettingsReducerState, action: Action): SettingsReducerState => (
			{ ...state, greenroomSettingsAccordionOpen: action.payload })],

	// general settings, not related to video
	[SET_MODERATED_QUESTIONS, (state: SettingsReducerState, action: Action): SettingsReducerState => (
		{ ...state, useModeratedQuestions: action.payload })],
	[SET_ASK_TO_JOIN, (state: SettingsReducerState, action: Action): SettingsReducerState => (
		{ ...state, askToJoin: action.payload })],
	[REPLACE_STATE, (state: SettingsReducerState, action: Action): SettingsReducerState => ({
		...state,
		...action.payload,
		settingsTab: state.settingsTab,
		featureFlags: state.featureFlags,
	})],
	[SET_SELECTED_BROADCAST_RESOLUTION,
		(state: SettingsReducerState, action: Action): SettingsReducerState => (
			{ ...state, selectedResolution: action.payload })],
	[SET_SETTINGS_TAB, (state: SettingsReducerState, action: Action): SettingsReducerState => (
		{ ...state, settingsTab: action.payload })],
	[SET_ENTRY_WITHOUT_PRODUCER,
		(state: SettingsReducerState, action: Action): SettingsReducerState => (
			{ ...state, entryWithoutProducer: action.payload })],
	[SET_BACKSTAGE_COMMS_ACTIVE,
		(state: SettingsReducerState, action: Action): SettingsReducerState => (
			{ ...state, backstageCommsActive: action.payload })],
	[SET_OVERLAY_VISIBLE, (state: SettingsReducerState, action: Action): SettingsReducerState => (
		{ ...state, overlayVisible: action.payload })],
	[SET_USE_DEFAULT_ENDPOINT,
		(state: SettingsReducerState, action: Action): SettingsReducerState => (
			{ ...state, useDefaultEndpoint: action.payload })],
	[SET_DEFAULT_RESOLUTION, (state: SettingsReducerState, action: Action): SettingsReducerState => (
		{ ...state, defaultResolution: action.payload })],
	[SET_USE_EMAIL_INVITES, (state: SettingsReducerState, action: Action): SettingsReducerState => (
		{ ...state, useEmailInvites: action.payload })],
	[SET_CLEAR_SHOT, (state: SettingsReducerState, action: Action): SettingsReducerState => (
		{ ...state, clearShot: action.payload })],
	[SET_DEFAULT_MEETING_IMAGE,
		(state: SettingsReducerState, action: Action): SettingsReducerState => (
			{ ...state, defaultMeetingImage: action.payload })],
	[SET_FEED_SCALING, (state: SettingsReducerState, action: Action): SettingsReducerState => (
		{ ...state, feedScaling: action.payload })],
	[SYNC_SETTINGS_REDUCER_STATE,
		(state: SettingsReducerState, action: Action): SettingsReducerState => (
			{ ...state, ...action.payload, hasSettingsFromBeehive: true })],
	[GET_FEATURE_FLAGS,
		(state: SettingsReducerState, action: Action): SettingsReducerState => handle(state, action, {
			success: (successState) => ({ ...successState }),
			failure: (failureState) => ({ ...failureState }),
			start: (startState) => ({ ...startState }),
			finish: (finishState) => (
				{
					...finishState,
					featureFlags: { ...action.payload },
					hasFetchedFeatureFlags: true,
				}),
		})],
	[GET_FEATURE_FLAG_OVERRIDES,
		(state: SettingsReducerState, action: Action): SettingsReducerState => handle(state, action, {
			success: (successState) => ({ ...successState }),
			failure: (failureState) => ({ ...failureState }),
			start: (startState) => ({ ...startState }),
			// NOTE Replacing FEATURE FLAGS not OVERRIDES
			finish: (finishState) => (
				{
					...finishState,
					featureFlags: { ...action.payload },
					hasFetchedFFOverrides: true,
				}),
		})],
	[UPDATE_FEATURE_FLAG, (state: SettingsReducerState, action: Action): SettingsReducerState => {
		const newff: any = { ...state.featureFlags };
		// eslint-disable-next-line prefer-destructuring
		newff[action.payload[0]] = action.payload[1];
		return { ...state, featureFlags: newff };
	}],
	[SET_HAS_SYNCED, (state: SettingsReducerState, action: Action): SettingsReducerState => (
		{ ...state, hasSynced: action.payload })],
	[SET_PRESENTER_AUDIENCE_TAB,
		(state: SettingsReducerState, action: Action): SettingsReducerState => (
			{ ...state, presenterAudienceTab: action.payload })],
]);

export const LEGACY_SYNCABLE_ACTIONS = [
	SET_MODERATED_QUESTIONS,
	SET_ASK_TO_JOIN,
	SET_BACKSTAGE_COMMS_ACTIVE,
	SET_USE_DEFAULT_ENDPOINT,
	SET_DEFAULT_RESOLUTION,
	SET_USE_EMAIL_INVITES,
	SET_FEED_SCALING,
	SET_SELECTED_BROADCAST_RESOLUTION,
];

export const SAVABLE_ACTIONS = [
	SET_ENTRY_WITHOUT_PRODUCER,
	SET_SETTINGS_TAB,
	SET_DEFAULT_MEETING_IMAGE,
	SET_PRESENTER_AUDIENCE_TAB,
];

export const canDoLegacySave = (action: Action, sock: Socket | null) => {
	if (
		sock
		&& actions.has(action.type)
		&& (
			SAVABLE_ACTIONS?.includes(action.type)
			|| LEGACY_SYNCABLE_ACTIONS?.includes(action.type)
		)
	) {
		return true;
	}
	return false;
};

export const canDoLegacySync = (action: Action, sock: Socket | null) => {
	if (
		sock
		&& actions.has(action.type)
		&& !action.remote
		&& LEGACY_SYNCABLE_ACTIONS?.includes(action.type)
	) {
		return true;
	}
	return false;
};

/* these are actions that have been migrated to use the new sync method */
export const MIGRATED_SYNCED_ACTIONS = Array.from(syncedReduxActions.keys());
const initialState = {
	type: 'none',
	payload: {},
};
export const SettingsReducer = (
	state: SettingsReducerState = initialSettingsState,
	action: Action = initialState,
): SettingsReducerState => {
	const newState = actions.get(action.type)?.(state, action) || state;

	if (canDoLegacySave(action, socket)) {
		clearTimeout(messageTimeout);

		messageTimeout = setTimeout(() => socket?.sendMessage('settings-update', newState), 250);

		// set a timeout to store the settings only after the current stack is empty
		clearTimeout(storageTimeout);

		storageTimeout = setTimeout(() => {
			// eslint-disable-next-line
			const store = require('../main');

			if (store.default) {
				const {
					SessionReducer: {
						token,
						meeting,
						host,
					},
					HomeReducer: {
						homeMeeting,
					},
				} = store.default.getState();

				// has Synced and feature flags are session specific, do not save
				// @ts-ignore
				const newStateToSave = structuredClone(newState);
				delete newStateToSave.hasSynced;
				delete newStateToSave.featureFlags;
				delete newStateToSave.hasSettingsFromBeehive;

				if (host && token && meeting) {
					SaveSettings(token, meeting.meeting, JSON.stringify(newStateToSave)).catch((e) => {
						console.error(e);
					});
				} else if (host && token && homeMeeting) {
					SaveSettings(token, homeMeeting.meeting, JSON.stringify(newStateToSave)).catch((e) => {
						console.error(e);
					});
				}
			}
		}, 500);
	}

	if (canDoLegacySync(action, socket)) {
		// eslint-disable-next-line
		const store = require('../main');

		if (store.default) {
			setTimeout(() => {
				const { SessionReducer: { host, session } } = store.default.getState();

				if (host) {
					socket?.sendMessage('settings-sync', { action, from: session });
				}
			});
		}
	}

	return newState;
};

export function setSettingsReducerSocket(sock: Socket): void {
	socket = sock;
}
